import { z } from 'zod';
import {
  avStatusSchema,
  categoryIdSchema,
  installScopeSchema,
  packageKindSchema,
  platformSchema,
  releaseStatusSchema,
  appOriginSchema,
  appStatusSchema,
  signatureStatusSchema,
} from '../platform.js';

export const cursorSchema = z.string().max(512).optional();

export const catalogQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  category: categoryIdSchema.optional(),
  platform: platformSchema.default('windows'),
  cursor: cursorSchema,
  limit: z.coerce.number().int().min(1).max(100).default(24),
  locale: z.enum(['en', 'ru']).default('en'),
});
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;

export const appSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  developerName: z.string().nullable(),
  category: categoryIdSchema,
  summary: z.string(),
  iconUrl: z.string().nullable(),
  origin: appOriginSchema,
  unverified: z.boolean(),
  latestVersion: z.string().nullable(),
  platforms: z.array(platformSchema),
});
export type AppSummary = z.infer<typeof appSummarySchema>;

export const catalogResponseSchema = z.object({
  items: z.array(appSummarySchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative(),
});
export type CatalogResponse = z.infer<typeof catalogResponseSchema>;

export const releaseViewSchema = z.object({
  id: z.string(),
  version: z.string(),
  platform: platformSchema,
  arch: z.array(z.string()),
  kind: packageKindSchema,
  scope: installScopeSchema,
  sizeBytes: z.number().int().nonnegative(),
  sha256: z.string(),
  downloadUrl: z.string().nullable(),
  silentArgs: z.array(z.string()).nullable(),
  upgradeArgs: z.array(z.string()).nullable(),
  minOs: z.string().nullable(),
  changelog: z.record(z.string().min(2).max(5), z.string()).nullable(),
  signatureStatus: signatureStatusSchema,
  avStatus: avStatusSchema,
  autoUpdateEligible: z.boolean(),
  status: releaseStatusSchema,
  publishedAt: z.number().int().nullable(),
});
export type ReleaseView = z.infer<typeof releaseViewSchema>;

export const appDetailSchema = appSummarySchema.extend({
  description: z.string(),
  homepageUrl: z.string().nullable(),
  license: z.string().nullable(),
  screenshots: z.array(z.string()),
  status: appStatusSchema,
  releases: z.array(releaseViewSchema),
  updatedAt: z.number().int(),
});
export type AppDetail = z.infer<typeof appDetailSchema>;

export const categoryViewSchema = z.object({
  id: categoryIdSchema,
  name: z.string(),
  count: z.number().int().nonnegative(),
});
export type CategoryView = z.infer<typeof categoryViewSchema>;

export const categoriesResponseSchema = z.object({
  items: z.array(categoryViewSchema),
});
export type CategoriesResponse = z.infer<typeof categoriesResponseSchema>;

/** Signed envelope for every feed response body. */
export const feedEnvelopeSchema = z.object({
  /** Key id used for `signature`; clients must find it in /v1/meta/keys. */
  kid: z.string(),
  /** Base64url Ed25519 signature over the canonical JSON of `payload`. */
  signature: z.string(),
  /** Signing key rotation counter, monotonically increasing. */
  serial: z.number().int().nonnegative(),
  payload: z.unknown(),
});
export type FeedEnvelope<T = unknown> = {
  kid: string;
  signature: string;
  serial: number;
  payload: T;
};

export const feedEntrySchema = z.object({
  appId: z.string(),
  name: z.string(),
  releaseId: z.string(),
  version: z.string(),
  platform: platformSchema,
  arch: z.array(z.string()),
  kind: packageKindSchema,
  sha256: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  downloadUrl: z.string(),
  silentArgs: z.array(z.string()).nullable(),
  upgradeArgs: z.array(z.string()).nullable(),
  scope: installScopeSchema,
  autoUpdateEligible: z.boolean(),
  signatureStatus: signatureStatusSchema,
  publishedAt: z.number().int(),
});
export type FeedEntry = z.infer<typeof feedEntrySchema>;

export const feedAppSchema = z.object({
  appId: z.string(),
  name: z.string(),
  releases: z.array(feedEntrySchema),
});
export type FeedApp = z.infer<typeof feedAppSchema>;

export const appFeedPayloadSchema = z.object({
  generatedAt: z.number().int(),
  app: feedAppSchema,
});
export type AppFeedPayload = z.infer<typeof appFeedPayloadSchema>;

export const feedIndexPayloadSchema = z.object({
  generatedAt: z.number().int(),
  cursor: z.string(),
  apps: z.array(
    z.object({
      appId: z.string(),
      updatedAt: z.number().int(),
      latestVersion: z.string().nullable(),
    }),
  ),
  nextSince: z.string().nullable(),
});
export type FeedIndexPayload = z.infer<typeof feedIndexPayloadSchema>;

export const metaKeysResponseSchema = z.object({
  keys: z.array(
    z.object({
      kid: z.string(),
      /** Base64 raw 32-byte Ed25519 public key. */
      publicKey: z.string(),
      active: z.boolean(),
      /** Unix seconds after which the key must no longer be trusted. */
      notAfter: z.number().int().nullable(),
    }),
  ),
  serial: z.number().int().nonnegative(),
});
export type MetaKeysResponse = z.infer<typeof metaKeysResponseSchema>;

export const metaResponseSchema = z.object({
  minApiVersion: z.string(),
  minClientVersion: z.string().nullable(),
  limits: z.object({
    maxUploadBytes: z.number().int().positive(),
    maxAppsPerDeveloper: z.number().int().positive(),
  }),
  categories: z.array(categoryIdSchema),
  locales: z.array(z.enum(['en', 'ru'])),
});
export type MetaResponse = z.infer<typeof metaResponseSchema>;

export const clientManifestPayloadSchema = z.object({
  generatedAt: z.number().int(),
  latest: z.object({
    version: z.string(),
    publishedAt: z.number().int(),
    releases: z.array(
      z.object({
        platform: platformSchema,
        arch: z.string(),
        kind: packageKindSchema,
        sha256: z.string(),
        sizeBytes: z.number().int().nonnegative(),
        downloadUrl: z.string(),
        minOs: z.string().nullable(),
      }),
    ),
    changelog: z.record(z.string().min(2).max(5), z.string()).nullable(),
  }),
});
export type ClientManifestPayload = z.infer<typeof clientManifestPayloadSchema>;

export const telemetryInstallSchema = z.object({
  appId: z.string().min(1).max(128),
  releaseId: z.string().min(1).max(128).optional(),
  /** Random per-install UUID; never derived from an account. */
  clientId: z.string().uuid(),
  operation: z.enum(['install', 'update', 'uninstall_attempt', 'rollback']),
  outcome: z.enum(['success', 'failure', 'cancelled']),
  errorCode: z.string().max(64).optional(),
  platform: platformSchema,
});
export type TelemetryInstall = z.infer<typeof telemetryInstallSchema>;

export const telemetryInstallBatchSchema = z.object({
  events: z.array(telemetryInstallSchema).min(1).max(50),
});
export type TelemetryInstallBatch = z.infer<typeof telemetryInstallBatchSchema>;
